import { ReduxStoreSlice } from "../../../Redux/ReduxStoreSlice";
import { BookingDriverDetails } from "../../../Services/BookingEntities";
import { BookingTrackingDetails, ExtendedEta } from "../../MyBookings/MyBookingEntities";
import { GetNewBookingState } from "../../MyBookings/MyBookingsDispatchV2";
import { PaymentOption } from "../../Payment/PaymentEntities";
import { PhoneToCnpBookingDetails } from "../PhoneToCnpEntities";
import { ErrorMessageType, NoticeType, ToasterType } from "../UI/Notifications/NotificationEntities";
import { PhoneToCnpState, PhoneToCnpInitialState, VoiceBookingUpdateState } from "./PhoneToCnpState";
import { BackingUserDetail } from "../../GuestPayment/GuestPaymentState";

/** Redux store slice for the Phone to CNP data. */
const slice = new ReduxStoreSlice<PhoneToCnpState>("PhoneToCnp", PhoneToCnpInitialState);

export const PhoneToCnpDispatch = {

    /** Show a notice whenever a specific event occurs and need to notified to user as a persistence notice. */
    ShowNotice: slice.Action("Show Notice", ShowNotice),

    /** Hide the active notice when the user closes the notice or if the status changed. */
    HideNotice: slice.EmptyAction("Hide Notice", HideNotice),

    /** Show a toaster whenever a specific event occurs and need to be notified to user as a transient notice. */
    ShowToaster: slice.Action("Show Toaster", ShowToaster),

    /** Hide the active toaster after it is displayed for a few seconds or if the status changed. */
    HideToaster: slice.EmptyAction("Hide Toaster", HideToaster),

    /** Show an error message when something goes wrong during the phone to CNP booking flow. */
    ShowErrorMessage: slice.Action("Show Error Message", ShowErrorMessage),

    /** Hide the active error message. Usually triggers once the user clicks on a button. */
    HideErrorMessage: slice.EmptyAction("Hide Error Message", HideErrorMessage),

    /** Refresh the booking details with the latest information. */
    RefreshBookingDetails: slice.Action("Refresh Booking Details", RefreshBookingDetails),

    /** Set the fare type to Fixed Price when the passenger selects the Fixed Price option. */
    SetFixedFare: slice.EmptyAction("Set Fixed Fare", SetFixedPrice),

    /** Clear the Fixed Price selection when the passenger switches back to the metered fare option. */
    ClearFixedFare: slice.EmptyAction("Clear Fixed Fare", ClearFixedPrice),

    /** Mark the Fixed Price payment as confirmed once the passenger has successfully completed the payment. */
    ConfirmPayment: slice.EmptyAction("Confirm Payment", ConfirmPayment),

    /** Set the payment card option selected by the passenger to pay for the Fixed Price fare. */
    PaymentOption: slice.Action("Set Payment Option", SetPaymentOption),

    /** Clear the selected payment option, e.g. when the passenger removes or changes their card selection. */
    ClearPaymentOption: slice.EmptyAction("Clear Payment Option", ClearPaymentOption),

    /** Set the loading state to true when starting to fetch booking details from the API. */
    StartLoadingBookingDetails: slice.EmptyAction("Start Loading Booking Details", StartLoadingBookingDetails),

    /** Set the loading state to false when the API call completes (success or failure). */
    StopLoadingBookingDetails: slice.EmptyAction("Stop Loading Booking Details", StopLoadingBookingDetails),

    /** Toggle the save card for future trips checkbox. */
    ToggleSaveCardForFutureTrips: slice.Action("Toggle Save Card For Future Trips", ToggleSaveCardForFutureTrips),

    /** Remove the card from the booking. Clears both Payment and SavedCard from the booking details, and clears the selected payment option. */
    RemoveExistingCard: slice.EmptyAction("Remove Existing Card", RemoveExistingCard),

    /** Store the GuestUserAuth credentials for the saved card's guest account, exclusively for the phone-to-CNP flow. Used to ensure DeleteCard is always authenticated with the correct credentials. */
    SetSavedCardGuestAuth: slice.Action("Set Saved Card Guest Auth", SetSavedCardGuestAuth),

    /** Refresh dynamic booking tracking details such as booking dispatch status, assigned vehicle etc. */
    RefreshTrackingInfo: slice.Action("Refresh Booking Tracking Info", RefreshBookingTrackingInfo),

    /** Refresh driver details of the booking after the booking is accepted by a driver. */
    RefreshDriverDetails: slice.Action("Refresh Driver Details", RefreshDriverDetails),

    /** Refresh ETA of a booking after the booking is accepted by a driver. We call Google directions API to get the ETA data. */
    RefreshEta: slice.Action("Refresh ETA", RefreshEta),

    /** Update booking with a CNP payment method is in progress. */
    UpdateInProgress: slice.EmptyAction("Update In Progress", UpdateInProgress),

    /** Update booking with a CNP payment method is succeeded. */
    UpdateSucceeded: slice.EmptyAction("Update Succeeded", UpdateSucceeded),

    /** Update booking with a CNP payment method is failed. */
    UpdateFailed: slice.EmptyAction("Update Failed", UpdateFailed),
}

/** Redux reducer for the Phone to CNP store slice. */
export const PhoneToCnpReducer = slice.MakeCombinedReducer();

/** Show a notice whenever a specific event occurs and need to notified to user as a persistence notice. */
function ShowNotice(state: PhoneToCnpState, noticeToShow: NoticeType): PhoneToCnpState {
    return {
        ...state,
        NoticeToDisplay: noticeToShow,
    }
}

/** Hide the active notice when the user closes the notice or if the status changed. */
function HideNotice(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        NoticeToDisplay: null,
    }
}

/** Show a toaster whenever a specific event occurs and need to be notified to user as a transient notice. */
function ShowToaster(state: PhoneToCnpState, toasterToShow: ToasterType): PhoneToCnpState {
    return {
        ...state,
        ToasterToDisplay: toasterToShow,
    }
}

/** Hide the active toaster after it is displayed for a few seconds or if the status changed. */
function HideToaster(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        ToasterToDisplay: null,
    }
}

/** Show an error message when something goes wrong during the phone to CNP booking flow. */
function ShowErrorMessage(state: PhoneToCnpState, errorMessageToShow: ErrorMessageType): PhoneToCnpState {
    return {
        ...state,
        ErrorMessage: errorMessageToShow
    };
}

/** Hide the active error message. Usually triggers once the user clicks on a button. */
function HideErrorMessage(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        ErrorMessage: null,
    }
}

/** Refresh the booking details with the latest information. */
function RefreshBookingDetails(state:PhoneToCnpState, bookingDetails: PhoneToCnpBookingDetails): PhoneToCnpState {
    return {
        ...state,
        Booking: bookingDetails
    }
}

/** Select the "FixedPrice" option for the booking fare */
function SetFixedPrice(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        IsFixedFareSelected: true
    }
}

/** Clear (undo) the "FixedPrice" selection for the booking fare */
function ClearFixedPrice(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        IsFixedFareSelected: false
    }
}

/** Mark the Fixed Price payment as confirmed once the passenger has successfully completed the payment. */
function ConfirmPayment(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        IsPaymentConfirmed: true
    }
}

/** Set the payment card option selected by the passenger to pay for the Fixed Price fare. */
function SetPaymentOption(state: PhoneToCnpState, paymentOption: PaymentOption): PhoneToCnpState {
    return {
        ...state,
        PaymentOption: paymentOption
    }
}

/** Clear the selected payment option, e.g. when the passenger removes or changes their card selection. */
function ClearPaymentOption(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        PaymentOption: null
    }
}

/** Set the loading state to true when starting to fetch booking details from the API. */
function StartLoadingBookingDetails(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        IsLoadingBookingDetails: true
    }
}

/** Set the loading state to false when the API call completes (success or failure). */
function StopLoadingBookingDetails(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        IsLoadingBookingDetails: false
    }
}

/** Toggle the save card for future trips checkbox. */
function ToggleSaveCardForFutureTrips(state: PhoneToCnpState, saveCard: boolean): PhoneToCnpState {
    return {
        ...state,
        SaveCardForFutureTrips: saveCard
    };
}

/** Remove the card from the booking. Clears both Payment and SavedCard (only one will be set at a time), and clears the selected payment option. */
function RemoveExistingCard(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        Booking: {
            ...state.Booking!,
            Payment: null,
            SavedCard: null
        },
        PaymentOption: null, // Also clear the selected payment option
        SavedCardGuestAuth: null // Clear the stored guest auth as the card is removed
    };
}

/** Store GuestUserAuth credentials for the saved card's guest account in the phone-to-CNP slice. */
function SetSavedCardGuestAuth(state: PhoneToCnpState, auth: BackingUserDetail): PhoneToCnpState {
    return {
        ...state,
        SavedCardGuestAuth: auth
    };
}

/** Refresh dynamic booking tracking details such as booking dispatch status, assigned vehicle etc. */
function RefreshBookingTrackingInfo(state: PhoneToCnpState, trackingInfo: BookingTrackingDetails): PhoneToCnpState {
    return {
        ...state,
        Booking: {
            ...state.Booking!,
            TrackingInfo: GetNewBookingState(state.Booking!.TrackingInfo, trackingInfo)
        }
    };
}

/** Refresh driver details of the booking after the booking is accepted by a driver. */
function RefreshDriverDetails(state: PhoneToCnpState, driverDetails: BookingDriverDetails): PhoneToCnpState {
    return {
        ...state,
        Booking: {
            ...state.Booking!,
            TrackingInfo: {
                ...state.Booking!.TrackingInfo,
                DriverName: driverDetails.DriverName,
                DriverImage: driverDetails.DriverImageURL
            }
        }
    }
}

/** Refresh ETA of a booking after the booking is accepted by a driver. We call Google directions API to get the ETA data. */
function RefreshEta(state: PhoneToCnpState, eta: ExtendedEta): PhoneToCnpState {
    return {
        ...state,
        Booking: {
            ...state.Booking!,
            Eta: eta
        }
    }
}

/** Update booking with a CNP payment method is in progress. */
function UpdateInProgress(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        PaymentUpdateState: VoiceBookingUpdateState.InProgress
    }
}

/** Update booking with a CNP payment method is succeeded. */
function UpdateSucceeded(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        PaymentUpdateState: VoiceBookingUpdateState.FinishedSuccess
    };
}

/** Update booking with a CNP payment method is failed. */
function UpdateFailed(state: PhoneToCnpState): PhoneToCnpState {
    return {
        ...state,
        PaymentUpdateState: VoiceBookingUpdateState.FinishedError
    };
}